import { useEffect, useState } from "react";
import { http } from "../lib/api";
import { Lock, Loader2, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = ["new", "contacted", "closed_won", "closed_lost"];
const STATUS_COLOR = {
  new: "var(--accent-amber)",
  contacted: "var(--accent-matrix)",
  closed_won: "var(--accent-matrix)",
  closed_lost: "var(--accent-flare)",
};

export const Admin = () => {
  const [key, setKey] = useState(() => sessionStorage.getItem("admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async (k) => {
    setBusy(true);
    try {
      const r = await http.get(`/admin/inquiries?key=${encodeURIComponent(k)}`);
      setInquiries(r.data.inquiries || []);
      setAuthed(true);
      sessionStorage.setItem("admin_key", k);
    } catch (e) {
      toast.error("wrong admin key");
      setAuthed(false);
    }
    setBusy(false);
  };

  // Intentional: auto-run once on mount if a saved key exists in sessionStorage.
  useEffect(() => { if (key) load(key); }, []);

  const updateStatus = async (id, status) => {
    try {
      await http.post(`/admin/inquiries/${id}/status?status=${status}&key=${encodeURIComponent(key)}`);
      setInquiries((rows) => rows.map((r) => r.id === id ? { ...r, status } : r));
      toast.success(`marked ${status}`);
    } catch {
      toast.error("update failed");
    }
  };

  if (!authed) {
    return (
      <div data-testid="admin-page" className="mx-auto max-w-md px-6 py-24">
        <div className="border border-white/10 bg-[#0a0a0a] p-8">
          <Lock className="w-5 h-5 text-[color:var(--accent-amber)] mb-4" />
          <h1 className="font-display font-bold text-2xl mb-2">owner only</h1>
          <p className="text-xs text-neutral-500 mb-6">Paste your ADMIN_KEY from backend/.env</p>
          <input
            data-testid="admin-key-input"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            className="w-full bg-black border border-white/10 focus:border-[color:var(--accent-amber)] outline-none px-3 py-2.5 text-sm mb-4"
            style={{ transitionProperty: "border-color", transitionDuration: "150ms" }}
            placeholder="admin key..."
            onKeyDown={(e) => e.key === "Enter" && load(key)}
          />
          <button
            data-testid="admin-unlock"
            onClick={() => load(key)}
            disabled={busy || !key}
            className="w-full bg-[color:var(--accent-amber)] text-black px-5 py-3 text-xs uppercase tracking-widest font-bold hover:bg-white disabled:opacity-50 duration-150"
            style={{ transitionProperty: "background-color, opacity" }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "unlock"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="mx-auto max-w-7xl px-6 py-16">
      <div className="mb-8 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-3">// admin // inquiries</div>
          <h1 className="font-display font-bold text-4xl tracking-tight">{inquiries.length} inquiries</h1>
        </div>
        <div className="flex items-center gap-2">
        <button
          data-testid="admin-refresh"
          onClick={() => load(key)}
          className="text-[10px] uppercase tracking-widest border border-white/15 hover:border-white/40 px-3 py-1.5 duration-150"
          style={{ transitionProperty: "border-color" }}
        >
          refresh
        </button>
        <button
          data-testid="admin-blog-sync"
          onClick={async () => {
            try {
              const r = await http.post(`/blog/sync?key=${encodeURIComponent(key)}`);
              toast.success(`blog synced: ${r.data.fetched} fetched, ${r.data.inserted} new, ${r.data.updated} updated`);
            } catch {
              toast.error("sync failed");
            }
          }}
          className="text-[10px] uppercase tracking-widest border border-[color:var(--accent-matrix)]/40 text-[color:var(--accent-matrix)] hover:border-[color:var(--accent-matrix)] px-3 py-1.5 duration-150"
          style={{ transitionProperty: "border-color" }}
        >
          sync blog
        </button>
        </div>
      </div>

      {inquiries.length === 0 ? (
        <div className="border border-dashed border-white/10 p-16 text-center text-neutral-500 text-sm">
          no inquiries yet. once someone submits the form on /business, it appears here.
        </div>
      ) : (
        <div className="space-y-3">
          {inquiries.map((q) => (
            <div key={q.id} data-testid={`admin-inquiry-${q.id}`} className="border border-white/10 bg-[#0a0a0a] p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <div className="text-[10px] uppercase tracking-widest px-2 py-0.5 border" style={{ color: STATUS_COLOR[q.status], borderColor: STATUS_COLOR[q.status] }}>
                    {q.status}
                  </div>
                  <div className="font-display font-bold text-[color:var(--accent-amber)]">{q.tier_name}</div>
                  <div className="text-xs text-neutral-500">${q.tier_price_usd?.toLocaleString()}</div>
                </div>
                <div className="text-[10px] text-neutral-500">{new Date(q.created_at).toLocaleString()}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mb-3">
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500">name</div>
                  <div>{q.name}</div>
                </div>
                <div className="flex items-center gap-1">
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500">email</div>
                    <div className="truncate">{q.email}</div>
                  </div>
                  <button
                    onClick={() => { navigator.clipboard.writeText(q.email); toast.success("copied"); }}
                    className="text-neutral-500 hover:text-white p-1"
                    style={{ transitionProperty: "color", transitionDuration: "150ms" }}
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                  <a href={`mailto:${q.email}?subject=Re%3A%20${encodeURIComponent(q.tier_name)}%20inquiry`}
                     className="text-neutral-500 hover:text-white p-1"
                     style={{ transitionProperty: "color", transitionDuration: "150ms" }}>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500">business</div>
                  <div>{q.business || "—"}</div>
                </div>
              </div>
              <div className="mb-3">
                <div className="text-[9px] uppercase tracking-widest text-neutral-500">description</div>
                <div className="text-sm text-neutral-300 whitespace-pre-wrap">{q.description}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid={`admin-status-${q.id}-${s}`}
                    onClick={() => updateStatus(q.id, s)}
                    disabled={q.status === s}
                    className={`text-[10px] uppercase tracking-widest px-2.5 py-1 border duration-150 disabled:opacity-100 disabled:cursor-default ${
                      q.status === s ? "text-black" : "text-neutral-400 border-white/10 hover:border-white/40"
                    }`}
                    style={q.status === s ? { background: STATUS_COLOR[s], borderColor: STATUS_COLOR[s] } : { transitionProperty: "color, border-color" }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
